import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import './NewAssistant.css';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import ColegioService from '../services/ColegioService';
import areaService from '../services/AreaService';
import materiaService from '../services/MateriaService';
import AsistenteService from '../services/AsistenteService';
import LoadingSpinner from './common/LoadingSpinner';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem, hasOpenAIChatsEnabled } from '../utils/adminMenu';

const INSTITUCIONAL_ID = '61A351C4-64C5-42A3-88D1-E27F092551CD';
const PROMPT_FEATURES = [
  'Usa exclusivamente los PDFs adjuntos (RAG obligatorio).',
  'Ejecuta file_search antes de responder consultas académicas.',
  'Si no hay evidencia en los PDFs, responde con el mensaje de falta de información.',
  'Respuestas académicas en HTML con citas de PDF y número de página.',
  'Si el usuario pide audio, la locución debe leer solo el contenido visible y no las etiquetas HTML.',
  'Puede responder saludos o uso del asistente sin citar PDFs.',
  'Imágenes o esquemas solo si existen en PDFs o son representaciones fieles con aviso.',
  'Puede armar planes de estudio y actuar como tutor orientado a ayudar al alumno.'
];

const AdminEditAsistente = () => {
  const navigate = useNavigate();
  const { assistantId } = useParams();
  const { user, logout } = useAuth();
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio seleccionado';

  const [form, setForm] = useState({
    idOpenAIAssistant: '',
    nombre: '',
    nicknameUrl: '',
    idTipoAsistente: INSTITUCIONAL_ID,
    descripcion: '',
    promptExtra: '',
    idColegio: activeColegioId || '',
    idArea: '',
    idMateria: '',
    idUnidad: '',
    publico: true,
    passwordAsistente: '',
    esPropio: false
  });
  const [areas, setAreas] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [unidadesLoading, setUnidadesLoading] = useState(false);
  const [unidadesError, setUnidadesError] = useState('');
  const [isFetchingAsistente, setIsFetchingAsistente] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const MAX_FILES = 20;
  const MAX_FILE_NAME_LENGTH = 40;

  const formatFileName = (name) => {
    if (!name) return '';
    return name.length > MAX_FILE_NAME_LENGTH ? `${name.slice(0, MAX_FILE_NAME_LENGTH)}...` : name;
  };

  const totalFilesCount = existingFiles.length + newFiles.length;

  useEffect(() => {
    if (!assistantId) {
      navigate('/admin-dashboard', { replace: true, state: { tab: 'asistentes' } });
    }
  }, [assistantId, navigate]);

  useEffect(() => {
    const fetchAreasMaterias = async () => {
      try {
        const [areasList, materiasList] = await Promise.all([
          areaService.getAreasActivas(activeColegioId),
          materiaService.getMateriasActivas()
        ]);
        const filteredMaterias = (materiasList || []).filter((m) => (m.idColegio || m.IdColegio) === activeColegioId);
        setAreas(Array.isArray(areasList) ? areasList : []);
        setMaterias(Array.isArray(filteredMaterias) ? filteredMaterias : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar áreas o materias.');
        setMessageType('error');
      }
    };
    if (activeColegioId && hasOpenAIChats) {
      fetchAreasMaterias();
    }
  }, [activeColegioId, hasOpenAIChats]);

  useEffect(() => {
    if (!form.idMateria) {
      setUnidades([]);
      setUnidadesError('');
      setUnidadesLoading(false);
      return;
    }

    let isMounted = true;
    setUnidadesLoading(true);
    setUnidadesError('');
    materiaService.getUnidades(form.idMateria)
      .then((list) => {
        if (!isMounted) return;
        setUnidades(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        if (!isMounted) return;
        setUnidadesError(error.message || 'No pudimos cargar las unidades.');
        setUnidades([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setUnidadesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [form.idMateria]);

  useEffect(() => {
    const fetchAsistente = async () => {
      if (!assistantId || !hasOpenAIChats) return;
      setIsFetchingAsistente(true);
      try {
        const response = await AsistenteService.getAsistenteById(assistantId);
        const data = response.asistente || response;
        setForm({
          idOpenAIAssistant: data.idOpenAIAssistant || assistantId,
          nombre: data.nombre || '',
          nicknameUrl: data.nicknameUrl || data.NicknameUrl || '',
          idTipoAsistente: INSTITUCIONAL_ID,
          descripcion: data.descripcion || '',
          promptExtra: data.promptExtra || data.PromptExtra || '',
          idColegio: data.idColegio || activeColegioId || '',
          idArea: data.idArea || '',
          idMateria: data.idMateria || '',
          idUnidad: data.idUnidad || data.IdUnidad || '',
          publico: data.publico ?? data.Publico ?? true,
          passwordAsistente: data.passwordAsistente || data.PasswordAsistente || '',
          esPropio: data.esPropio ?? data.EsPropio ?? false
        });
        if (data?.archivos && Array.isArray(data.archivos)) {
          setExistingFiles(
            data.archivos.map(archivo => ({
              fileId: archivo.id,
              fileName: archivo.nombreArchivo,
              fechaAlta: archivo.fechaAlta
            }))
          );
        } else {
          setExistingFiles([]);
        }
        setNewFiles([]);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el asistente.');
        setMessageType('error');
      } finally {
        setIsFetchingAsistente(false);
      }
    };

    fetchAsistente();
  }, [assistantId, activeColegioId, hasOpenAIChats]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMateriaChange = (value) => {
    setForm((prev) => ({
      ...prev,
      idMateria: value,
      idUnidad: ''
    }));
  };

  const handleAddFiles = (files) => {
    if (!files || files.length === 0) return;

    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    if (pdfFiles.length !== files.length) {
      setMessage('Solo se permiten archivos PDF.');
      setMessageType('error');
    }

    setNewFiles(prev => {
      const availableSlots = MAX_FILES - (existingFiles.length + prev.length);
      if (availableSlots <= 0) {
        setMessage('Se alcanzó el límite máximo de archivos (20).');
        setMessageType('error');
        return prev;
      }

      const filesToAdd = pdfFiles.slice(0, availableSlots);
      if (filesToAdd.length < pdfFiles.length) {
        setMessage(`Solo se pueden agregar ${availableSlots} archivos adicionales.`);
        setMessageType('warning');
      }

      return [...prev, ...filesToAdd];
    });
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasOpenAIChats) {
      setMessage('Este colegio no tiene habilitada la feature flag PermiteChatsOpenAI.');
      setMessageType('error');
      return;
    }
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del asistente.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área.');
      setMessageType('error');
      return;
    }
    if (!form.publico && !form.passwordAsistente.trim()) {
      setMessage('Completa el password del asistente.');
      setMessageType('error');
      return;
    }

    if (newFiles.length > 0 && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    submitUpdate();
  };

  const submitUpdate = async () => {
    setLoading(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', form.nombre.trim());
      formDataToSend.append('idTipoAsistente', INSTITUCIONAL_ID);
      formDataToSend.append('descripcion', form.descripcion || '');
      formDataToSend.append('promptExtra', form.promptExtra || '');
      formDataToSend.append('nicknameUrl', form.nicknameUrl || '');
      formDataToSend.append('idColegio', activeColegioId);
      formDataToSend.append('idArea', form.idArea);
      if (form.idMateria) {
        formDataToSend.append('idMateria', form.idMateria);
      }
      if (form.idMateria) {
        formDataToSend.append('idUnidad', form.idUnidad || '');
      }
      formDataToSend.append('publico', String(form.publico));
      formDataToSend.append('conPassword', String(!form.publico));
      formDataToSend.append('esPropio', String(form.esPropio));
      if (!form.publico && form.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', form.passwordAsistente.trim());
      }
      newFiles.forEach(file => {
        formDataToSend.append('archivosPdf', file);
      });

      await AsistenteService.actualizarAsistente(assistantId, formDataToSend);
      setMessage('Asistente actualizado correctamente.');
      setMessageType('success');
      setShowConfirmation(false);
      setNewFiles([]);
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'asistentes' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el asistente.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const disabledContent = (
    <div className="content-card">
      <div style={{ padding: '2.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', color: '#dc2626', marginBottom: '1rem' }}>
          <i className="fas fa-ban"></i>
        </div>
        <h2 style={{ margin: '0 0 0.75rem' }}>Chats OpenAI deshabilitados</h2>
        <p style={{ margin: '0 0 1.5rem', color: '#475569' }}>
          Este colegio no tiene habilitada la feature flag <strong>PermiteChatsOpenAI</strong>.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/admin-dashboard', { replace: true, state: { tab: 'resumen' } })}
        >
          <i className="fas fa-arrow-left"></i>
          <span>Volver al dashboard</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="new-dashboard admin-mode">
      {isFetchingAsistente && (
        <LoadingSpinner message={`Obteniendo datos del asistente${form.nombre ? ` ${form.nombre}` : ''}...`} />
      )}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="asistentes"
          user={user}
          featureFlags={featureFlags}
          baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar asistente OpenAI</h1>
            <p className="page-subtitle">Ajusta la información del asistente en el colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          {!hasOpenAIChats ? disabledContent : (
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-robot" />
                <span>Datos del asistente</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                El área es obligatoria; la materia puede quedar vacía.
              </p>
              <form onSubmit={handleSubmit} className="assistant-form">
                <div className="assistant-form-grid">
                  <div className="assistant-form-panel">
                    <div className="form-section basic-info-section">
                      <div className="section-header">
                        <div className="section-icon">
                          <i className="fas fa-robot"></i>
                        </div>
                        <div className="section-text">
                          <h3 className="section-title">Datos del asistente</h3>
                          <p className="section-description">Actualizá la información principal del asistente.</p>
                        </div>
                      </div>

                      <div className="form-grid">
                  <div className="form-field">
                    <label>ID OpenAI Assistant</label>
                    <input type="text" value={form.idOpenAIAssistant} disabled />
                  </div>
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Asistente X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Nickname URL</label>
                    <input
                      type="text"
                      value={form.nicknameUrl}
                      onChange={(e) => handleChange('nicknameUrl', e.target.value)}
                      placeholder="ej: algebra-lineal-1"
                    />
                  </div>
                  <div className="form-field">
                    <label>Área (obligatorio)</label>
                    <select value={form.idArea} onChange={(e) => handleChange('idArea', e.target.value)}>
                      <option value="">Selecciona un área</option>
                      {areas.map((a) => (
                        <option key={a.id || a.Id} value={a.id || a.Id}>
                          {a.nombre || a.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Materia (opcional)</label>
                    <select value={form.idMateria} onChange={(e) => handleMateriaChange(e.target.value)}>
                      <option value="">Sin materia</option>
                      {materias.map((m) => (
                        <option key={m.id || m.Id} value={m.id || m.Id}>
                          {m.nombre || m.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Unidad (opcional)</label>
                    <select
                      value={form.idUnidad}
                      onChange={(e) => handleChange('idUnidad', e.target.value)}
                      disabled={!form.idMateria || unidadesLoading}
                    >
                      <option value="">Sin unidad</option>
                      {unidades.map((u) => {
                        const uid = u.id || u.Id;
                        const nombre = u.nombre || u.Nombre;
                        const activo = u.activo ?? u.Activo ?? true;
                        return (
                          <option key={uid} value={uid}>
                            {activo ? nombre : `${nombre} (Inactiva)`}
                          </option>
                        );
                      })}
                    </select>
                    {!form.idMateria && (
                      <small style={{ color: '#64748b' }}>Selecciona una materia para ver sus unidades.</small>
                    )}
                    {unidadesLoading && (
                      <small style={{ color: '#64748b' }}>Cargando unidades...</small>
                    )}
                    {unidadesError && (
                      <small style={{ color: '#b91c1c' }}>{unidadesError}</small>
                    )}
                  </div>
                      </div>

                      <div className="form-field">
                        <label>Descripción</label>
                        <textarea
                          value={form.descripcion}
                          onChange={(e) => handleChange('descripcion', e.target.value)}
                          placeholder="Descripción opcional"
                          style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                        />
                      </div>

                      <div className="form-grid">
                        <div className="form-field">
                          <label>Visibilidad</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <input
                              type="checkbox"
                              checked={form.publico}
                              onChange={(e) => {
                                const nextValue = e.target.checked;
                                setForm((prev) => ({
                                  ...prev,
                                  publico: nextValue,
                                  passwordAsistente: nextValue ? '' : prev.passwordAsistente
                                }));
                              }}
                            />
                            <span>Asistente público</span>
                          </div>
                        </div>
                      </div>

                      {!form.publico && (
                        <div className="form-field">
                          <label>Password</label>
                          <input
                            type="text"
                            value={form.passwordAsistente}
                            onChange={(e) => handleChange('passwordAsistente', e.target.value)}
                            placeholder="Ingresa el password (obligatorio)"
                          />
                        </div>
                      )}

                      <div className="form-field">
                        <label>Uso del asistente</label>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.9rem',
                            alignItems: 'flex-start',
                            padding: '1rem 1.1rem',
                            borderRadius: '14px',
                            border: `1px solid ${form.esPropio ? '#fdba74' : '#bfdbfe'}`,
                            background: form.esPropio ? '#fff7ed' : '#eff6ff'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={form.esPropio}
                            onChange={(e) => handleChange('esPropio', e.target.checked)}
                          />
                          <div style={{ display: 'grid', gap: '0.3rem' }}>
                            <strong style={{ color: '#0f172a' }}>Asistente propio</strong>
                            <span style={{ color: '#475569', lineHeight: 1.5 }}>
                              {form.esPropio
                                ? 'Este asistente será solo para uso personal del docente. No podrá compartirse con alumnos por medio del aula.'
                                : 'Este asistente podrá ser visible para los estudiantes por medio del aula, según la configuración restante del asistente.'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-section files-section" style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid var(--gray-200)', borderRadius: '10px', background: '#fafafa' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className="fas fa-file-pdf" style={{ color: '#dc2626' }}></i>
                      Archivos del asistente
                    </h4>

                  <div className="existing-files-panel" style={{ marginBottom: '1rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontWeight: 500 }}>
                      Archivos actuales ({existingFiles.length})
                    </p>
                    {existingFiles.length === 0 ? (
                      <p style={{ color: '#64748b', fontStyle: 'italic', margin: 0 }}>No hay archivos cargados.</p>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: '1.25rem', listStyle: 'none' }}>
                        {existingFiles.map((file) => (
                          <li key={file.fileId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                            <i className="fas fa-file-pdf" style={{ color: '#dc2626' }}></i>
                            <span>{formatFileName(file.fileName)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''}`}
                    style={{
                      border: `2px dashed ${isDragOver ? '#3b82f6' : '#cbd5e1'}`,
                      borderRadius: '10px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      background: isDragOver ? '#eff6ff' : '#fff',
                      transition: 'all 0.2s'
                    }}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragOver(false);
                      const files = Array.from(e.dataTransfer.files);
                      handleAddFiles(files);
                    }}
                  >
                    <i className="fas fa-cloud-upload-alt" style={{ fontSize: '2rem', color: '#64748b', marginBottom: '0.5rem' }}></i>
                    <p style={{ margin: '0 0 0.75rem', color: '#475569' }}>
                      Arrastrá archivos PDF o seleccionalos desde tu equipo
                    </p>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="file"
                        multiple
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          handleAddFiles(files);
                          e.target.value = '';
                        }}
                      />
                      <span className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        <i className="fas fa-folder-open"></i>
                        Seleccionar PDFs
                      </span>
                    </label>
                    <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                      Máximo 20 archivos · Solo PDF
                    </p>
                  </div>

                  {newFiles.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ margin: '0 0 0.5rem', fontWeight: 500 }}>
                        Nuevos archivos seleccionados ({newFiles.length})
                      </p>
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                        {newFiles.map((file, index) => (
                          <li key={`${file.name}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', background: '#fff', borderRadius: '6px', marginBottom: '0.35rem', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <i className="fas fa-file-pdf" style={{ color: '#dc2626' }}></i>
                              <span>{formatFileName(file.name)}</span>
                              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewFile(index)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0.25rem' }}
                              title="Eliminar archivo"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <p style={{ margin: '1rem 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                    Total de archivos: {totalFilesCount}/20
                  </p>
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {loading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                </div>

                <aside className="assistant-prompt-panel">
                  <div className="prompt-panel-header">
                    <i className="fas fa-clipboard-list"></i>
                    <div>
                      <h3>Comportamiento del asistente</h3>
                      <p>Resumen del prompt base configurado.</p>
                    </div>
                  </div>
                  <ul className="prompt-features-list">
                    {PROMPT_FEATURES.map((item) => (
                      <li key={item}>
                        <i className="fas fa-check-circle"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="prompt-extra-field">
                    <label className="form-label">
                      <i className="fas fa-pen"></i>
                      Prompt extra (opcional)
                    </label>
                    <textarea
                      className="form-input enhanced"
                      rows="6"
                      value={form.promptExtra}
                      onChange={(e) => handleChange('promptExtra', e.target.value)}
                      placeholder="Aclaraciones adicionales para el asistente"
                    />
                    <div className="input-hint">
                      <i className="fas fa-info-circle"></i>
                      Estas reglas se agregan al prompt base.
                    </div>
                  </div>
                  <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'asistentes' } })}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Guardar cambios</span>
                    </button>
                  </div>
                </aside>
              </form>
            </div>
          </div>
          )}
        </div>
      </main>

      {showConfirmation && (
        <div className="confirm-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="confirm-modal" style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '420px', width: '90%' }}>
            <h3 style={{ margin: '0 0 1rem' }}>Reemplazar archivos del asistente</h3>
            <p style={{ margin: '0 0 1.25rem', color: '#475569' }}>
              Se actualizará el asistente, reemplazando todos sus archivos actuales por los nuevos que acabas de adjuntar.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmation(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { setShowConfirmation(false); submitUpdate(); }}
              >
                Entiendo, actualizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditAsistente;
